import React from 'react'
import { Checkbox, Dropdown, Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { useTabletAndBelowMediaQuery } from 'layout'
import { formatShortDate, lastContactedLabel } from 'utility/crm/dates'
import { Avatar, Card, Muted, Row, StagePill } from './ui'

const ClickableRow = styled(Table.Row)`
    cursor: pointer;
    &:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: -2px;
    }
`

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const NextAction = ({ task }) =>
    task ? (
        <span>
            {task.Title}{' '}
            <Muted style={task.IsOverdue ? { color: '#dc2626', opacity: 1, fontWeight: 600 } : undefined}>
                {task.DueDate ? `(${formatShortDate(task.DueDate)})` : ''}
            </Muted>
        </span>
    ) : (
        <Muted>No follow-up set</Muted>
    )

const ariaSort = (query, column) => {
    if (query.Sort !== column) return 'none'
    return query.Dir === 'desc' ? 'descending' : 'ascending'
}

const COLUMNS = [
    { key: 'name', label: 'Name', sort: 'name' },
    { key: 'company', label: 'Company', sort: 'company' },
    { key: 'stage', label: 'Stage', sort: 'stage' },
    { key: 'last', label: 'Last contacted', sort: 'last' },
    { key: 'next', label: 'Next action', sort: 'next' },
]

/**
 * Contacts as a sortable table (desktop) or a list of cards (phones). Rows open with Enter.
 * @param {{
 *   items: import('utility/crm/types').CrmContact[],
 *   query: import('utility/crm/types').CrmContactsQuery,
 *   selected: Set<number>,
 *   isAdmin: boolean,
 *   onSort: (column: string) => void,
 *   onToggle: (id: number) => void,
 *   onTogglePage: (checked: boolean) => void,
 *   onOpen: (contact: import('utility/crm/types').CrmContact) => void,
 *   onEdit: (contact: import('utility/crm/types').CrmContact) => void,
 *   onDelete: (contact: import('utility/crm/types').CrmContact) => void,
 * }} props
 */
export const ContactsTable = ({ items, query, selected, isAdmin, onSort, onToggle, onTogglePage, onOpen, onEdit, onDelete }) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const allOnPage = items.length > 0 && items.every(c => selected.has(c.ContactId))
    const someOnPage = items.some(c => selected.has(c.ContactId))

    const rowMenu = contact => (
        <Dropdown icon="ellipsis vertical" direction="left" aria-label={`Actions for ${contact.Name}`} className="icon" onClick={e => e.stopPropagation()}>
            <Dropdown.Menu>
                <Dropdown.Item icon="folder open" text="Open" onClick={() => onOpen(contact)} />
                <Dropdown.Item icon="edit" text="Edit" onClick={() => onEdit(contact)} />
                {isAdmin ? <Dropdown.Item icon="trash" text="Delete" onClick={() => onDelete(contact)} /> : null}
            </Dropdown.Menu>
        </Dropdown>
    )

    const onKey = (event, contact) => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter') onOpen(contact)
        if (event.key === ' ') {
            event.preventDefault()
            onToggle(contact.ContactId)
        }
    }

    if (isMobile) {
        return (
            <CardList>
                {items.map(contact => (
                    <Card
                        key={contact.ContactId}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${contact.Name}`}
                        onClick={() => onOpen(contact)}
                        onKeyDown={event => onKey(event, contact)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Row $between $gap={8}>
                            <Row $gap={10}>
                                <Checkbox
                                    checked={selected.has(contact.ContactId)}
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => onToggle(contact.ContactId)}
                                    aria-label={`Select ${contact.Name}`}
                                />
                                <Avatar name={contact.Name} />
                                <div>
                                    <strong>{contact.Name}</strong>
                                    <div>
                                        <Muted>{[contact.Title, contact.AccountName].filter(Boolean).join(' · ')}</Muted>
                                    </div>
                                </div>
                            </Row>
                            {rowMenu(contact)}
                        </Row>
                        <Row $gap={8} $wrap style={{ marginTop: 8 }}>
                            <StagePill stage={contact.Stage} />
                            <Muted>{lastContactedLabel(contact.LastContacted)}</Muted>
                        </Row>
                        <div style={{ marginTop: 6 }}>
                            <NextAction task={contact.NextTask} />
                        </div>
                    </Card>
                ))}
            </CardList>
        )
    }

    return (
        <Table sortable celled selectable compact="very" aria-label="Contacts">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell collapsing>
                        <Checkbox
                            checked={allOnPage}
                            indeterminate={!allOnPage && someOnPage}
                            onChange={(_, d) => onTogglePage(Boolean(d.checked))}
                            aria-label="Select all contacts on this page"
                        />
                    </Table.HeaderCell>
                    {COLUMNS.map(col => (
                        <Table.HeaderCell
                            key={col.key}
                            sorted={ariaSort(query, col.sort) === 'none' ? null : ariaSort(query, col.sort)}
                            onClick={() => onSort(col.sort)}
                            aria-sort={ariaSort(query, col.sort)}
                        >
                            {col.label}
                        </Table.HeaderCell>
                    ))}
                    <Table.HeaderCell>Owner</Table.HeaderCell>
                    <Table.HeaderCell collapsing>
                        <span className="visually-hidden" style={{ position: 'absolute', left: -9999 }}>
                            Actions
                        </span>
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {items.map(contact => (
                    <ClickableRow
                        key={contact.ContactId}
                        tabIndex={0}
                        aria-label={`Open ${contact.Name}`}
                        onClick={() => onOpen(contact)}
                        onKeyDown={event => onKey(event, contact)}
                    >
                        <Table.Cell collapsing onClick={e => e.stopPropagation()}>
                            <Checkbox
                                checked={selected.has(contact.ContactId)}
                                onChange={() => onToggle(contact.ContactId)}
                                aria-label={`Select ${contact.Name}`}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            <Row $gap={10}>
                                <Avatar name={contact.Name} />
                                <div>
                                    <strong>{contact.Name}</strong>
                                    {contact.Title ? (
                                        <div>
                                            <Muted>{contact.Title}</Muted>
                                        </div>
                                    ) : null}
                                </div>
                            </Row>
                        </Table.Cell>
                        <Table.Cell>{contact.AccountName}</Table.Cell>
                        <Table.Cell>
                            <StagePill stage={contact.Stage} />
                        </Table.Cell>
                        <Table.Cell>{lastContactedLabel(contact.LastContacted)}</Table.Cell>
                        <Table.Cell>
                            <NextAction task={contact.NextTask} />
                        </Table.Cell>
                        <Table.Cell>
                            {contact.OwnerName ? <Avatar name={contact.OwnerName} size={26} /> : <Muted>–</Muted>}
                        </Table.Cell>
                        <Table.Cell collapsing>{rowMenu(contact)}</Table.Cell>
                    </ClickableRow>
                ))}
            </Table.Body>
        </Table>
    )
}
